import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import Login from '../features/auth/LoginPage';
import LandingPage from '../features/landing/LandingPage';
import NotFoundPage from './NotFoundPage';
import { AuthProvider } from './AuthContext';
import PrivateRoute from './PrivateRoute';
import MemberRedirect from './MemberRedirect';
import { ThemeProvider } from './ThemeProvider';
import ErrorBoundary from './ErrorBoundary';
import { ConfirmProvider } from '../components/ConfirmDialog';
import { ToastContainer } from 'react-toastify';
import PageLoading from './PageLoading';
import lazyPage from './lazyPage';
import 'react-toastify/dist/ReactToastify.css';
import { initGlobalErrorLogging } from '../features/admin/auditLogger';

// The landing and sign-in pages load with the app; every other page is downloaded the first time
// it is opened, so visitors don't pay for the admin panel, maps, charts and editors up front.
const CreateAcct = lazyPage(() => import('../features/auth/CreateAccountPage'));
const UserQuestions = lazyPage(() => import('../features/auth/OnboardingQuestions'));
const Home = lazyPage(() => import('../features/home/HomePage'));
const Locate = lazyPage(() => import('../features/locate/LocatePage'));
const Profile = lazyPage(() => import('../features/profile/ProfilePage'));
const Inbox = lazyPage(() => import('../features/inbox/InboxPage'));
const Opportunities = lazyPage(() => import('../features/opportunities/OpportunitiesPage'));
const BusinessDashboard = lazyPage(() => import('../features/business/BusinessDashboard'));
const Admin = lazyPage(() => import('../features/admin/AdminPage'));
const CommunitiesPage = lazyPage(() => import('../features/communities/CommunitiesPage'));
const CommunityDetailPage = lazyPage(() => import('../features/communities/CommunityDetailPage'));
const SearchPage = lazyPage(() => import('../features/search/SearchPage'));
const BusinessLandingPage = lazyPage(() => import('../features/landing/BusinessLandingPage'));
const TermsPage = lazyPage(() => import('../features/legal/TermsPage'));
const PrivacyPage = lazyPage(() => import('../features/legal/PrivacyPage'));
const GuidelinesPage = lazyPage(() => import('../features/legal/GuidelinesPage'));
const Settings = lazyPage(() => import('../features/settings/SettingsPage'));

function App() {
  useEffect(() => {
    initGlobalErrorLogging();
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <ThemeProvider>
            <ConfirmProvider>
              <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/login" element={<MemberRedirect><Login /></MemberRedirect>} />
                <Route path="/createAccount" element={<CreateAcct />} />
                <Route path="/" element={<MemberRedirect><LandingPage /></MemberRedirect>} />
                <Route path="/business" element={<BusinessLandingPage />} />
                <Route path="/userQuestions" element={<PrivateRoute component={UserQuestions} />} />
                <Route path="/inbox" element={<PrivateRoute component={Inbox} />} />
                <Route path="/locate" element={<PrivateRoute component={Locate} />} />
                <Route path="/profile" element={<PrivateRoute component={Profile} />} />
                <Route path="/search" element={<PrivateRoute component={SearchPage} />} />
                <Route path="/communities" element={<PrivateRoute component={CommunitiesPage} />} />
                <Route path="/communities/:communityId" element={<PrivateRoute component={CommunityDetailPage} />} />
                <Route path="/opportunities" element={<Opportunities />} />
                <Route path="/feed" element={<PrivateRoute component={Home} />} />
                <Route path="/business/dashboard" element={<PrivateRoute component={BusinessDashboard} />} />
                <Route path="/settings" element={<PrivateRoute component={Settings} />} />
                <Route path="/system-admin" element={<PrivateRoute component={Admin} />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/guidelines" element={<GuidelinesPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </Suspense>
              <ToastContainer position="top-center" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            </ConfirmProvider>
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;